import { useQuery, UseQueryResult } from '@tanstack/react-query';

// Type definitions
export interface ImageData {
  public_id: string;
  secure_url: string;
  url?: string;
  width: number;
  height: number;
  format: string;
  created_at: string;
  original_filename?: string;
  tags?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
}

export interface SectionMetadata {
  name: string;
  path: string;
}

export interface WebsiteSection {
  _metadata: SectionMetadata;
  _images: ImageData[];
}

export interface WebsiteData {
  [key: string]: WebsiteSection;
}

export interface PortfolioData {
  _images?: ImageData[]; // Root level images
  Website?: WebsiteData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Custom hook to fetch and access Website data from JSON file
 * @param jsonPath - Path to your JSON file (e.g., '/portfolio-images.json')
 * @returns TanStack Query result with Website data
 */
export const useWebsiteData = (
  jsonPath: string = '/portfolio-images.json'
): UseQueryResult<WebsiteData, Error> => {
  return useQuery<WebsiteData, Error>({
    queryKey: ['websiteData', jsonPath],
    queryFn: async () => {
      const response = await fetch(jsonPath);

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data: PortfolioData = await response.json();

      // Extract only the Website section
      return data.Website || {};
    },
    staleTime: 60 * 60 * 1000, // Data stays fresh for 1 hour (longer since it's static)
    gcTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
  });
};

/**
 * Custom hook to get the entire portfolio data (including root images)
 * @param jsonPath - Path to your JSON file
 * @returns TanStack Query result with full portfolio data
 */
export const usePortfolioData = (
  jsonPath: string = '/portfolio-images.json'
): UseQueryResult<PortfolioData, Error> => {
  return useQuery<PortfolioData, Error>({
    queryKey: ['portfolioData', jsonPath],
    queryFn: async () => {
      const response = await fetch(jsonPath);

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      return await response.json();
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
};

/**
 * Helper hook to get a specific section from Website data
 * @param sectionName - Name of the section (e.g., 'Home_In_Dublin', 'Kitchen')
 * @param jsonPath - Path to your JSON file
 * @returns TanStack Query result with specific section data
 */
export const useWebsiteSection = (
  sectionName: string | null,
  jsonPath: string = '/portfolio-images.json'
): UseQueryResult<WebsiteSection | null, Error> => {
  return useQuery<WebsiteSection | null, Error>({
    queryKey: ['websiteSection', sectionName, jsonPath],
    queryFn: async () => {
      const response = await fetch(jsonPath);

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data: PortfolioData = await response.json();

      // Return specific section
      return data.Website?.[sectionName as string] || null;
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    enabled: !!sectionName, // Only run query if sectionName is provided
  });
};

/**
 * Helper hook to get all section names
 * @param jsonPath - Path to your JSON file
 * @returns TanStack Query result with array of section names
 */
export const useWebsiteSectionNames = (
  jsonPath: string = '/portfolio-images.json'
): UseQueryResult<string[], Error> => {
  return useQuery<string[], Error>({
    queryKey: ['websiteSectionNames', jsonPath],
    queryFn: async () => {
      const response = await fetch(jsonPath);

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data: PortfolioData = await response.json();

      // Return array of section names
      return Object.keys(data.Website || {});
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
};

/**
 * Helper hook to get images filtered by tag
 * @param tag - Tag to filter by
 * @param sectionName - Optional section name to filter within
 * @param jsonPath - Path to your JSON file
 * @returns TanStack Query result with filtered images
 */
export const useImagesByTag = (
  tag: string,
  sectionName?: string,
  jsonPath: string = '/portfolio-images.json'
): UseQueryResult<ImageData[], Error> => {
  return useQuery<ImageData[], Error>({
    queryKey: ['imagesByTag', tag, sectionName, jsonPath],
    queryFn: async () => {
      const response = await fetch(jsonPath);

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data: PortfolioData = await response.json();

      let images: ImageData[] = [];

      if (sectionName && data.Website?.[sectionName]) {
        // Get images from specific section
        images = data.Website[sectionName]._images || [];
      } else if (data.Website) {
        // Get all images from all Website sections
        images = Object.values(data.Website).flatMap(
          (section) => section._images || []
        );
      }

      // Filter by tag
      return images.filter((img) => img.tags?.includes(tag));
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    enabled: !!tag,
  });
};

/**
 * Helper hook to get all unique tags from the portfolio
 * @param jsonPath - Path to your JSON file
 * @returns TanStack Query result with array of unique tags
 */
export const useAllTags = (
  jsonPath: string = '/portfolio-images.json'
): UseQueryResult<string[], Error> => {
  return useQuery<string[], Error>({
    queryKey: ['allTags', jsonPath],
    queryFn: async () => {
      const response = await fetch(jsonPath);

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data: PortfolioData = await response.json();

      const tagsSet = new Set<string>();

      // Collect tags from root images
      data._images?.forEach((img) => {
        img.tags?.forEach((tag) => tagsSet.add(tag));
      });

      // Collect tags from Website sections
      if (data.Website) {
        Object.values(data.Website).forEach((section) => {
          section._images?.forEach((img) => {
            img.tags?.forEach((tag) => tagsSet.add(tag));
          });
        });
      }

      return Array.from(tagsSet).sort();
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
};
